import React, { memo, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import sx from './styles'

/** Ruta pública del JSON de la animación (se sirve desde /public). */
const ANIMATION_PATH = '/lottie/flame.json'

/**
 * Margen para que la animación arranque un poco antes de entrar en pantalla y
 * no se vea el salto de "aparece parada y luego se mueve".
 */
const OBSERVER_ROOT_MARGIN = '200px'

/**
 * Proporción del ancho que se recorta con margen negativo hacia la izquierda.
 *
 * El arte viene centrado en un lienzo cuadrado de 800x800, pero la flama es
 * alta y angosta: a los lados queda espacio transparente. Sin esta compensación
 * la animación se ve despegada de la etiqueta aunque el `gap` sea cero. Como el
 * hueco crece con el tamaño, se descuenta en proporción y no en píxeles fijos.
 */
const ARTBOARD_SIDE_PADDING = 0.22

/**
 * Flama animada (Lottie) que acompaña a la etiqueta de "Descuento" en las
 * tarjetas del SRP.
 *
 * Tres decisiones que valen la pena explicar, porque el SRP pinta hasta 24
 * tarjetas por página y una animación por tarjeta se paga cara:
 *
 * 1. `lottie-web` se importa de forma dinámica y sólo cuando la tarjeta está por
 *    entrar en pantalla. Si el usuario nunca ve una unidad con descuento, no
 *    descarga los ~46 KB del reproductor.
 * 2. El JSON vive en `/public` y se carga por `path`, no empaquetado: así no
 *    engorda el bundle de JS y el navegador lo cachea entre tarjetas (se
 *    descarga una sola vez, ~22 KB comprimido).
 * 3. Un `IntersectionObserver` destruye la instancia al salir de pantalla, de
 *    modo que sólo hay animaciones vivas para las tarjetas visibles.
 *
 * Además respeta `prefers-reduced-motion`: si el usuario pidió menos animación
 * en su sistema, no se carga nada.
 *
 * @param {number|object} size Lado de la flama en px (acepta valores responsive de MUI).
 */
const FlameAnimation = ({ size = 20 }) => {
  const containerRef = useRef(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    // Accesibilidad: quien pidió menos animación no recibe nada, ni la descarga.
    const prefersReducedMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return undefined

    let animation = null
    let cancelled = false

    const destroy = () => {
      if (animation) {
        animation.destroy()
        animation = null
      }
    }

    const load = async () => {
      // Ya cargada (o el efecto se limpió mientras se resolvía el import).
      if (animation || cancelled) return

      try {
        // `lottie_light` es UMD: según la interop el objeto puede venir en
        // `default` o ser el módulo mismo, así que se contemplan ambos.
        const mod = await import('lottie-web/build/player/lottie_light')
        const lottie = mod.default || mod
        if (cancelled || !containerRef.current) return

        animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          path: ANIMATION_PATH,
        })
      } catch (e) {
        // Que falle una animación decorativa no debe romper el listado.
      }
    }

    // Sin soporte del observador (navegadores viejos): se carga sin diferir.
    if (typeof IntersectionObserver !== 'function') {
      load()
      return () => {
        cancelled = true
        destroy()
      }
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) load()
        else destroy()
      })
    }, { rootMargin: OBSERVER_ROOT_MARGIN })

    observer.observe(container)

    return () => {
      cancelled = true
      observer.disconnect()
      destroy()
    }
  }, [])

  return (
    <Box
      ref={containerRef}
      // Decorativa: la etiqueta "Descuento" de al lado ya da el significado.
      aria-hidden="true"
      sx={{
        ...sx.root,
        width: size,
        height: size,
        // Sólo se compensa cuando el tamaño es numérico; con valores
        // responsive de MUI se deja el espaciado al contenedor.
        ...(typeof size === 'number'
          ? { ml: `${-Math.round(size * ARTBOARD_SIDE_PADDING)}px` }
          : {}),
      }}
    />
  )
}

export default memo(FlameAnimation)
